import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Loader2, Mail, MousePointerClick, AlertOctagon, Eye, RefreshCw, PauseOctagon, PlayCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRESETS = [
  { label: '24h', days: 1 },
  { label: '7d',  days: 7 },
  { label: '30d', days: 30 },
];

const CAMPAIGN_LABELS = {
  'review': 'Post-purchase review request',
  'lead': 'Lead Finder cold outreach',
  'outreach': 'Partner 1:1 outreach',
  'totw': 'Tool of the Week newsletter',
  'finish': 'Profile-completion drip',
  'welcome': 'Welcome / onboarding',
  'order': 'Order confirmations',
  'drip': 'Lifecycle drip',
  '(untagged)': 'Transactional (no tag)',
  '(none)': 'Transactional (no tag)',
};

function fmtPct(rate) {
  if (rate === null || rate === undefined) return '—';
  return `${(rate * 100).toFixed(2)}%`;
}

function rateColor(rate, kind) {
  // For bounce/complaint, lower is better. For open/click, higher is better.
  if (kind === 'danger') {
    if (rate >= 0.03) return 'text-red-400';
    if (rate >= 0.015) return 'text-orange-400';
    return 'text-emerald-400';
  }
  if (kind === 'success') {
    if (rate >= 0.20) return 'text-emerald-400';
    if (rate >= 0.08) return 'text-sky-400';
    return 'text-neutral-400';
  }
  return 'text-neutral-300';
}

export default function AdminLifecycleEmails() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unpauseLoading, setUnpauseLoading] = useState(false);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [metricsRes, ratesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/lifecycle-email-metrics?days=${days}`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/admin/email-safety/rates?hours=24`, { headers: authHeaders() }),
      ]);
      setData(metricsRes.data);
      setRates(ratesRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [days]);

  const handleUnpause = async () => {
    if (!window.confirm('Unpause all auto-paused Lead Finder campaigns? Only do this after cleaning the bad lead list — otherwise the guardrail will re-pause them in 30 min.')) return;
    setUnpauseLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/lead-finder/safety/unpause`,
        {},
        { headers: authHeaders() }
      );
      toast.success(`Unpaused ${res.data.unpaused_campaigns} campaign(s)`);
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to unpause');
    } finally {
      setUnpauseLoading(false);
    }
  };

  const handleRunGuardrail = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/email-safety/run`,
        {},
        { headers: authHeaders() }
      );
      if (res.data.paused_campaigns > 0) {
        toast.error(`Guardrail tripped — paused ${res.data.paused_campaigns} campaign(s)`);
      } else {
        toast.success(`All good — ${res.data.reason || 'rates within safe thresholds'}`);
      }
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to run guardrail');
    } finally {
      setLoading(false);
    }
  };

  const bounceRate = rates?.bounce_rate || 0;
  const complaintRate = rates?.complaint_rate || 0;
  const bounceDanger = bounceRate >= 0.03;
  const complaintDanger = complaintRate >= 0.001;

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="admin-lifecycle-emails">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Mail className="w-6 h-6" />
              Lifecycle Email Metrics
            </h1>
            <p className="text-sm text-neutral-400 mt-1">
              Click / open / bounce rates across every email category we send. Powered by Resend webhooks.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.days}
                onClick={() => setDays(p.days)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  days === p.days
                    ? 'bg-[#00F0FF] text-black'
                    : 'bg-neutral-900 text-neutral-300 hover:bg-neutral-800 border border-white/10'
                }`}
                data-testid={`preset-${p.days}`}
              >
                {p.label}
              </button>
            ))}
            <button
              onClick={loadData}
              className="ml-2 p-2 rounded-full bg-neutral-900 hover:bg-neutral-800 border border-white/10"
              title="Refresh"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 text-neutral-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Safety guardrail status — last 24h rates */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className={`rounded-2xl border p-5 ${
            bounceDanger ? 'bg-red-500/5 border-red-500/30' : 'bg-neutral-900/50 border-white/10'
          }`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-neutral-400 font-semibold">
                  24h bounce rate
                </p>
                <p className={`text-3xl font-bold mt-1 ${rateColor(bounceRate, 'danger')}`} data-testid="bounce-rate">
                  {fmtPct(bounceRate)}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  Domain-safe ceiling: 3%
                </p>
              </div>
              {bounceDanger && (
                <AlertOctagon className="w-6 h-6 text-red-400" />
              )}
            </div>
          </div>

          <div className={`rounded-2xl border p-5 ${
            complaintDanger ? 'bg-red-500/5 border-red-500/30' : 'bg-neutral-900/50 border-white/10'
          }`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-neutral-400 font-semibold">
                  24h complaint rate
                </p>
                <p className={`text-3xl font-bold mt-1 ${rateColor(complaintRate, 'danger')}`} data-testid="complaint-rate">
                  {fmtPct(complaintRate)}
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  Gmail hard ceiling: 0.1%
                </p>
              </div>
              {complaintDanger && (
                <AlertOctagon className="w-6 h-6 text-red-400" />
              )}
            </div>
          </div>
        </div>

        {/* Guardrail actions */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Button
            onClick={handleRunGuardrail}
            disabled={loading}
            variant="outline"
            className="border-white/15 text-white hover:bg-white/10"
            data-testid="run-guardrail-btn"
          >
            <PauseOctagon className="w-4 h-4 mr-2" />
            Run safety check now
          </Button>
          <Button
            onClick={handleUnpause}
            disabled={unpauseLoading}
            variant="outline"
            className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
            data-testid="unpause-btn"
          >
            {unpauseLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <PlayCircle className="w-4 h-4 mr-2" />
            )}
            Unpause auto-paused campaigns
          </Button>
        </div>

        {/* Per-campaign metrics */}
        <div className="bg-neutral-900/50 rounded-2xl border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">
              Per-campaign breakdown · last {days} day{days !== 1 ? 's' : ''}
            </h2>
            {data?.campaigns?.length === 0 && (
              <span className="text-xs text-neutral-500">No events yet</span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-950 text-neutral-400">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Campaign type</th>
                  <th className="text-right px-4 py-3 font-semibold">Delivered</th>
                  <th className="text-right px-4 py-3 font-semibold">Opens</th>
                  <th className="text-right px-4 py-3 font-semibold">Clicks</th>
                  <th className="text-right px-4 py-3 font-semibold">Bounces</th>
                  <th className="text-right px-4 py-3 font-semibold">Complaints</th>
                  <th className="text-right px-4 py-3 font-semibold">Click rate</th>
                  <th className="text-right px-4 py-3 font-semibold">Bounce rate</th>
                </tr>
              </thead>
              <tbody>
                {(data?.campaigns || []).map((row) => (
                  <tr key={row.campaign} className="border-t border-white/5 hover:bg-neutral-900/40">
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">
                        {CAMPAIGN_LABELS[row.campaign] || row.campaign}
                      </div>
                      <div className="text-[10px] text-neutral-500 font-mono mt-0.5">
                        tag: {row.campaign}*
                      </div>
                    </td>
                    <td className="text-right px-4 py-3 text-neutral-200 font-mono">{row.delivered}</td>
                    <td className="text-right px-4 py-3 text-sky-400 font-mono flex items-center justify-end gap-1">
                      <Eye className="w-3 h-3 opacity-50" />
                      {row.opened}
                    </td>
                    <td className="text-right px-4 py-3 text-emerald-400 font-mono">
                      <span className="inline-flex items-center gap-1 justify-end">
                        <MousePointerClick className="w-3 h-3 opacity-60" />
                        {row.clicked}
                      </span>
                    </td>
                    <td className={`text-right px-4 py-3 font-mono ${row.bounced > 0 ? 'text-orange-400' : 'text-neutral-500'}`}>
                      {row.bounced}
                    </td>
                    <td className={`text-right px-4 py-3 font-mono ${row.complained > 0 ? 'text-red-400' : 'text-neutral-500'}`}>
                      {row.complained}
                    </td>
                    <td className={`text-right px-4 py-3 font-mono ${rateColor(row.click_rate, 'success')}`}>
                      {fmtPct(row.click_rate)}
                    </td>
                    <td className={`text-right px-4 py-3 font-mono ${rateColor(row.bounce_rate, 'danger')}`}>
                      {fmtPct(row.bounce_rate)}
                    </td>
                  </tr>
                ))}
                {!loading && data?.campaigns?.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-10 text-neutral-500 text-sm">
                      No webhook events received in this window. Send some emails and check back.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-neutral-500 mt-4">
          Bounce/complaint rates are 24h running. Per-campaign table uses the selected window.
          Click rates only show for emails sent from a domain with click-tracking CNAME verified in Resend.
        </p>
      </div>
    </AdminLayout>
  );
}
